import {
    useNavigate,
    NavLink,
} from "react-router-dom";

import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";

import {
    Bell,
    Check,
    CheckCircle2,
    CheckCheck,
    LayoutDashboard,
    Users,
    CalendarCheck2,
    CalendarX2,
    Coffee,
    ClipboardList,
    CheckSquare,
    Home,
    Megaphone,
    MessageCircle,
    LogOut,
    Moon,
    Sun,
    Menu,
    X,
    Calendar,
    MapPin,
    IndianRupee,
    Gauge,
    Inbox,
    ShieldAlert,
    BriefcaseBusiness,
    ShieldCheck,
    Settings,
    Search,
    Paperclip,
    Send,
    Download,
    FileSpreadsheet,
    FileBarChart,
    FileText,
    UserCheck,
    QrCode,
    Camera,
    Sparkles,
} from "lucide-react";
import SuperAdminRequestModal from "../common/SuperAdminRequestModal";

import {
    useAdminAuth,
} from "../../context/AdminAuthContext";
import { useMeta } from "../../context/MetaContext";

import {
    useTheme,
} from "../../context/ThemeContext";

import api from "../../api/axios";

const notificationToneClass = {
    danger: "bg-red-500",
    warning: "bg-amber-500",
    celebrate: "bg-emerald-500",
    info: "bg-[var(--accent-primary)]",
};

let attendancePrefetchStarted = false;
const prefetchAdminAttendance = () => {
    if (attendancePrefetchStarted) return;
    attendancePrefetchStarted = true;
    const now = new Date();
    const date = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
    ].join("-");

    Promise.allSettled([
        api.get("/api/admin/employees/all", { params: { includeEscalations: false } }),
        api.get("/api/admin/attendance", {
            params: {
                startDate: date,
                endDate: date,
                includeAbsenceSummary: false,
                includeEscalations: false,
            },
        }),
    ]).then((results) => {
        if (results.every((result) => result.status === "rejected")) {
            attendancePrefetchStarted = false;
        }
    });
};

const ADMIN_ROUTE_PREFETCH = {
    "/admin/dashboard": "/api/admin/dashboard-summary",
    "/admin/control-center": "/api/admin/control-center",
    "/admin/payroll": "/api/admin/salary-slips/period-status",
    "/admin/approval-inbox": "/api/admin/approval-inbox",
    "/admin/hr": "/api/admin/hr/dashboard",
    "/admin/general-letters": "/api/admin/general-letters",
    "/admin/sales-live-tracking": "/api/admin/sales-live-tracking",
    "/admin/lunch-alerts": "/api/admin/lunch-alerts",
    "/admin/eod": "/api/admin/eod",
    "/admin/complaints": "/api/admin/complaints",
    "/admin/offboarding": "/api/admin/offboarding",
    "/admin/policies": "/api/admin/policies",
    "/admin/leave": "/api/admin/leave",
    "/admin/leave-settings": "/api/settings",
    "/admin/expenses": "/api/admin/expenses",
    "/admin/relieved-employees": "/api/admin/relieved-employees",
    "/admin/finance": "/api/admin/finance/dashboard",
};

const prefetchAdminRoute = (route) => {
    if (route === "/admin/attendance") {
        prefetchAdminAttendance();
        return;
    }
    const now = new Date();
    const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const periodKey = date.slice(0, 7);
    if (route === "/admin/employees") {
        api.get("/api/admin/employees", { params: { page: 1, search: "", team: "", role: "", status: "", documents: "", includeEscalations: false, includeStats: false } }).catch(() => {});
        return;
    }
    if (route === "/admin/hr") {
        api.get("/api/admin/hr/dashboard", { params: { view: "dashboard", limit: 8, periodKey } }).catch(() => {});
        return;
    }
    if (route === "/admin/general-letters") {
        api.get("/api/admin/general-letters", { params: { limit: 30 } }).catch(() => {});
        return;
    }
    if (route === "/admin/sales-live-tracking") {
        api.get("/api/admin/sales-live-tracking", { params: { date }, cacheTtlMs: 30000 }).catch(() => {});
        return;
    }
    if (route === "/admin/lunch-alerts") {
        api.get("/api/admin/lunch-alerts", { params: { status: "all", date }, cacheTtlMs: 30000 }).catch(() => {});
        return;
    }
    const endpoint = ADMIN_ROUTE_PREFETCH[route];
    if (endpoint) api.get(endpoint).catch(() => {});
};

const isServiceUnavailableError = (err) => {
    const message = String(err?.message || "");
    return (
        err?.response?.status === 503
        || String(err?.code || "") === "ERR_NETWORK"
        || message.includes("503")
        || message.includes("Network Error")
        || message.includes("ERR_CONNECTION_RESET")
    );
};

const useIsDesktopNav = () => {
    const [isDesktop, setIsDesktop] = useState(() =>
        typeof window === "undefined" ? true : window.matchMedia("(min-width: 1280px)").matches,
    );

    useEffect(() => {
        const media = window.matchMedia("(min-width: 1280px)");
        const handleChange = () => setIsDesktop(media.matches);
        handleChange();
        media.addEventListener("change", handleChange);
        return () => media.removeEventListener("change", handleChange);
    }, []);

    return isDesktop;
};

const ADMIN_CHAT_CACHE_KEY = "admin_chat_cache_v3";
const CHAT_TIME_ZONE = "Asia/Kolkata";

const chatDateParts = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return new Intl.DateTimeFormat("en-CA", {
        timeZone: CHAT_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date).reduce((parts, part) => {
        if (part.type !== "literal") parts[part.type] = part.value;
        return parts;
    }, {});
};

const chatDateKey = (value) => {
    const parts = chatDateParts(value);
    return parts ? `${parts.year}-${parts.month}-${parts.day}` : "";
};

const chatDateLabel = (value) => {
    const messageDate = new Date(value);
    if (Number.isNaN(messageDate.getTime())) return "Unknown date";

    const todayParts = chatDateParts(new Date());
    const messageKey = chatDateKey(messageDate);
    const todayKey = todayParts ? `${todayParts.year}-${todayParts.month}-${todayParts.day}` : "";
    const yesterday = new Date(Date.UTC(
        Number(todayParts?.year),
        Number(todayParts?.month) - 1,
        Number(todayParts?.day) - 1,
    ));
    const yesterdayKey = yesterday.toISOString().slice(0, 10);

    if (messageKey === todayKey) return "Today";
    if (messageKey === yesterdayKey) return "Yesterday";

    return new Intl.DateTimeFormat("en-IN", {
        timeZone: CHAT_TIME_ZONE,
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(messageDate);
};

const readStoredChatCache = (key) => {
    try {
        return JSON.parse(sessionStorage.getItem(key) || "{}");
    } catch {
        sessionStorage.removeItem(key);
        return {};
    }
};

const writeStoredChatCache = (key, patch) => {
    try {
        const current = readStoredChatCache(key);
        sessionStorage.setItem(key, JSON.stringify({ ...current, ...patch, savedAt: Date.now() }));
    } catch {
        // Chat should still work if storage is disabled or full.
    }
};

const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Could not read this file. Please choose it again."));
        reader.readAsDataURL(file);
    });

const uploadChatFile = async (file) => {
    const token = localStorage.getItem("admin_token");
    const uploadBinary = async (url) => {
        const res = await fetch(url, {
            method: "POST",
            credentials: "include",
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                "Content-Type": "application/octet-stream",
                "X-File-Name": encodeURIComponent(file.name),
                "X-File-Type": file.type || "application/octet-stream",
                "X-File-Size": String(file.size),
            },
            body: file,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(`${res.status} ${data.message || res.statusText || url}`);
        return data;
    };

    const uploadJson = async (url, dataUrl) => {
        const res = await api.post(
            url,
            {
                attachment: {
                    name: file.name,
                    type: file.type || "application/octet-stream",
                    size: file.size,
                    dataUrl,
                },
            },
            { skipSaveToast: true },
        );
        return res.data;
    };

    const errors = [];
    try {
        return await uploadBinary("/api/chat/attachments/binary");
    } catch (err) {
        errors.push(`binary shared: ${err.message}`);
    }

    const dataUrl = await readFileAsDataUrl(file);
    for (const url of ["/api/chat/attachments", "/api/admin/chat/attachments"]) {
        try {
            return await uploadJson(url, dataUrl);
        } catch (err) {
            errors.push(`${url}: ${err.response?.status || ""} ${err.response?.data?.message || err.message}`);
        }
    }

    throw new Error(`File upload failed. ${errors.join(" | ")}`);
};

const buildInstantAttachmentUrl = (source, token) => {
    if (!source || String(source).startsWith("data:")) return source;
    if (String(source).startsWith("blob:")) return source;
    const backendBase = String(api.defaults.baseURL || "").replace(/\/+$/, "");
    const absoluteSource = String(source).startsWith("/api/")
        ? `${backendBase}${source}`
        : String(source);
    if (!token || !absoluteSource.includes("/api/chat/attachments/")) return absoluteSource;

    const separator = absoluteSource.includes("?") ? "&" : "?";
    return `${absoluteSource}${separator}chatToken=${encodeURIComponent(token)}`;
};

const getChatAttachmentUrl = (attachment, tokenKey) => {
    const source = attachment?.url || attachment?.dataUrl || attachment?.localUrl;
    if (!source) return "";

    const token = localStorage.getItem(tokenKey);
    return buildInstantAttachmentUrl(source, token);
};

const fetchChatAttachmentBlob = async (attachment, tokenKey, signal) => {
    const source = attachment?.url || attachment?.dataUrl || attachment?.localUrl;
    if (!source) return null;

    const token = localStorage.getItem(tokenKey);
    const headers = token && !String(source).startsWith("data:")
        ? { Authorization: `Bearer ${token}` }
        : {};
    const res = await fetch(getChatAttachmentUrl(attachment, tokenKey), {
        credentials: "include",
        headers,
        signal,
    });

    if (!res.ok) {
        const detail = await res.json().catch(() => null);
        throw new Error(detail?.message ? `Could not open file (${res.status}): ${detail.message}` : `Could not open file (${res.status})`);
    }

    return res.blob();
};

const isImageAttachment = (attachment) => String(attachment?.type || "").startsWith("image/");
const isPdfAttachment = (attachment) =>
    String(attachment?.type || "").includes("pdf")
    || String(attachment?.name || "").toLowerCase().endsWith(".pdf");
const isExcelAttachment = (attachment) => {
    const type = String(attachment?.type || "").toLowerCase();
    const name = String(attachment?.name || "").toLowerCase();
    return type.includes("spreadsheet")
        || type.includes("excel")
        || type.includes("csv")
        || [".xlsx", ".xls", ".csv"].some((extension) => name.endsWith(extension));
};

const isGenericParticipantName = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    return !normalized || normalized === "employee" || normalized === "admin";
};

const shortParticipantId = (id) => String(id || "").slice(-6).toUpperCase();

const participantDisplayName = (participant = {}) => {
    if (!isGenericParticipantName(participant.name)) return participant.name;
    if (participant.email) return participant.email;
    if (participant.employeeId) return participant.employeeId;

    const suffix = shortParticipantId(participant.id);
    if (participant.missingUser) {
        return `${participant.model === "Admin" ? "Former admin" : "Former employee"}${suffix ? ` ${suffix}` : ""}`;
    }

    return `${participant.model || "Participant"}${suffix ? ` ${suffix}` : ""}`;
};

const messagePreview = (message = {}) => {
    const attachment = message.attachment || message.originalAttachment;
    if (message.deletedForEveryone) return "Message unsent";
    return message.message || attachment?.name || (attachment ? "Attachment" : "");
};

const conversationPreview = (conversation = {}, cachedMessages = []) => {
    if (conversation.lastMessage) return conversation.lastMessage;
    const latest = cachedMessages[cachedMessages.length - 1];
    return messagePreview(latest) || "No messages yet";
};

const downloadChatAttachment = async (attachment, tokenKey) => {
    const blob = await fetchChatAttachmentBlob(attachment, tokenKey);
    if (!blob) return;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment?.name || "attachment";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const mergeLocalSendingMessages = (serverMessages, cachedMessages = []) => {
    const serverIds = new Set(serverMessages.map((message) => message._id));
    const localMessages = cachedMessages.filter((message) =>
        (message.pending || message.failed || String(message._id || "").startsWith("temp-"))
        && !serverIds.has(message._id),
    );
    return [...serverMessages, ...localMessages];
};

const AdminNotificationBell = () => {
    const navigate = useNavigate();
    const wrapRef = useRef(null);
    const loadingRef = useRef(false);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ count: 0, items: [] });

    const fetchNotifications = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setLoading(true);
        try {
            const res = await api.get("/api/admin/notifications");
            setData(res.data || { count: 0, items: [] });
        } catch (err) {
            setData({ count: 0, items: [] });
            if (!isServiceUnavailableError(err)) {
                console.error("Failed to load notifications:", err);
            }
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
        const id = window.setInterval(fetchNotifications, 30000);
        return () => window.clearInterval(id);
    }, [fetchNotifications]);

    useEffect(() => {
        const handleClick = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const handleItem = (item) => {
        setOpen(false);
        if (item.route) navigate(item.route);
    };

    return (
        <div ref={wrapRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                className="group relative h-11 w-11 rounded-full border border-amber-300 bg-amber-400 text-white shadow-lg shadow-amber-400/30 hover:-translate-y-0.5 hover:border-amber-400 hover:bg-amber-500 hover:shadow-amber-400/45 dark:border-amber-300/50 dark:bg-amber-500 dark:text-white dark:hover:bg-amber-400 flex items-center justify-center transition-all"
                title="Notifications"
            >
                <Bell size={19} strokeWidth={2.4} />
                {data.count > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-black leading-none flex items-center justify-center ring-2 ring-white dark:ring-[#111111] shadow-sm">
                        {data.count > 9 ? "9+" : data.count}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-12 w-[320px] max-w-[calc(100vw-2rem)] bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl shadow-2xl z-[120] overflow-visible">
                    <div className="p-4 border-b border-[var(--border-color)] flex items-center justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase text-[var(--accent-primary)]">Notifications</p>
                            <h3 className="font-bold">{data.count} pending</h3>
                        </div>
                        <button type="button" onClick={fetchNotifications} className="text-xs font-bold text-[var(--text-secondary)]">
                            {loading ? "Loading" : "Refresh"}
                        </button>
                    </div>
                    <div className="max-h-[360px] overflow-y-auto p-2">
                        {!data.items?.length ? (
                            <div className="p-5 text-center text-sm text-[var(--text-secondary)]">
                                <CheckCircle2 size={22} className="mx-auto mb-2 text-emerald-500" />
                                All clear
                            </div>
                        ) : (
                            data.items.map((item, index) => (
                                <button
                                    key={item.id || `${item.type || "notification"}-${item.route || item.title || index}-${index}`}
                                    type="button"
                                    onClick={() => handleItem(item)}
                                    className="w-full text-left p-3 rounded-xl hover:bg-[var(--bg-secondary)] transition-all flex gap-3"
                                >
                                    <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${notificationToneClass[item.tone] || notificationToneClass.info}`} />
                                    <span className="min-w-0">
                                        <span className="block text-sm font-bold truncate">{item.title}</span>
                                        <span className="block text-xs text-[var(--text-secondary)] line-clamp-2">{item.body}</span>
                                    </span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminChatPanel = () => {
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const storedChatRef = useRef(readStoredChatCache(ADMIN_CHAT_CACHE_KEY));
    const cachedConversations = storedChatRef.current.conversations || [];
    const cachedSelected = storedChatRef.current.selectedConversation || cachedConversations[0] || null;
    const messageCacheRef = useRef(storedChatRef.current.messagesByConversation || {});
    const selectedConversationIdRef = useRef(null);
    const attachmentUploadRef = useRef({});
    const fileInputRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [conversations, setConversations] = useState(cachedConversations);
    const [directory, setDirectory] = useState(storedChatRef.current.directory || []);
    const [selectedConversation, setSelectedConversation] = useState(cachedSelected);
    const [messages, setMessages] = useState(messageCacheRef.current[cachedSelected?._id] || []);
    const [draft, setDraft] = useState("");
    const [attachment, setAttachment] = useState(null);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [previewFullScreen, setPreviewFullScreen] = useState(false);
    const [previewFileUrl, setPreviewFileUrl] = useState("");
    const [previewFileError, setPreviewFileError] = useState("");
    const [previewFileLoading, setPreviewFileLoading] = useState(false);
    const [previewSheet, setPreviewSheet] = useState(null);
    const [attachmentLoading, setAttachmentLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [chatView, setChatView] = useState("inbox");
    const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
    const [chatSearch, setChatSearch] = useState("");
    const [exportFrom, setExportFrom] = useState("");
    const [exportTo, setExportTo] = useState("");
    const [exporting, setExporting] = useState(false);
    const [mobileTab, setMobileTab] = useState(cachedSelected ? "chat" : "list");

    const unreadTotal = conversations.reduce((sum, conversation) => sum + Number(conversation.unreadCount || 0), 0);
    const isAdminConversation = (conversation) =>
        conversation?.participants?.some((participant) => participant.model === "Admin");
    const isSuperAdminConversation = (conversation) =>
        conversation?.participants?.some(
            (participant) => participant.role === "superadmin" || String(participant.name || "").toLowerCase().includes("super admin"),
        );
    const participantLabel = (conversation) => {
        if (conversation?.title && conversation.title !== "Conversation") {
            return conversation.title;
        }
        const participantNames = (conversation?.participants || [])
            .map(participantDisplayName)
            .filter(Boolean);
        return participantNames.length ? participantNames.join(", ") : "Conversation";
    };
    const inboxConversations = useMemo(() => conversations.filter(isAdminConversation), [conversations]);
    const monitorConversations = useMemo(() => conversations.filter((conversation) => !isAdminConversation(conversation)), [conversations]);
    const visibleConversations = useMemo(() => chatView === "inbox" ? inboxConversations : monitorConversations, [chatView, inboxConversations, monitorConversations]);
    const normalizedChatSearch = chatSearch.trim().toLowerCase();
    const filteredDirectory = useMemo(() => {
        if (!normalizedChatSearch) return directory;
        return directory.filter((user) =>
            [
                user.name,
                user.employeeId,
                user.email,
                user.team,
                user.role,
            ].some((value) => String(value || "").toLowerCase().includes(normalizedChatSearch)),
        );
    }, [directory, normalizedChatSearch]);
    const filteredVisibleConversations = useMemo(() => {
        if (!normalizedChatSearch) return visibleConversations;
        return visibleConversations.filter((conversation) =>
            [
                participantLabel(conversation),
                conversation.title,
                conversation.lastMessage,
                ...(conversation.participants || []).flatMap((participant) => [
                    participant.name,
                    participant.email,
                    participant.employeeId,
                    participant.team,
                    participant.role,
                ]),
            ].some((value) => String(value || "").toLowerCase().includes(normalizedChatSearch)),
        );
    }, [normalizedChatSearch, visibleConversations]);
    const selectedIsVisible = selectedConversation && visibleConversations.some((conversation) => conversation._id === selectedConversation._id);

    useEffect(() => {
        selectedConversationIdRef.current = selectedConversation?._id || null;
    }, [selectedConversation?._id]);

    useEffect(() => {
        if (!previewAttachment || (!isImageAttachment(previewAttachment) && !isPdfAttachment(previewAttachment) && !isExcelAttachment(previewAttachment))) {
            setPreviewFileUrl("");
            setPreviewFileError("");
            setPreviewFileLoading(false);
            setPreviewSheet(null);
            return undefined;
        }

        const controller = new AbortController();
        let objectUrl = "";
        setPreviewFileLoading(true);
        setPreviewFileError("");
        setPreviewFileUrl("");
        setPreviewSheet(null);

        fetchChatAttachmentBlob(previewAttachment, "admin_token", controller.signal)
            .then(async (blob) => {
                const url = URL.createObjectURL(blob);
                objectUrl = url;
                setPreviewFileUrl(url);
                if (isExcelAttachment(previewAttachment)) {
                    const workbook = XLSX.read(await blob.arrayBuffer(), { type: "array" });
                    const firstSheetName = workbook.SheetNames[0];
                    const rows = firstSheetName
                        ? XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], { header: 1, blankrows: false })
                        : [];
                    setPreviewSheet({
                        name: firstSheetName || "Sheet 1",
                        rows: rows.slice(0, 80).map((row) => row.slice(0, 20)),
                        totalRows: rows.length,
                    });
                }
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    setPreviewFileError(error.message || "Could not open this attachment.");
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setPreviewFileLoading(false);
            });

        return () => {
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [previewAttachment]);

    const fetchConversations = useCallback(async () => {
        try {
            const res = await api.get("/api/admin/chat/conversations");
            const list = Array.isArray(res.data) ? res.data : [];
            setConversations(list);
            setSelectedConversation((current) => {
                const next = current || list[0] || null;
                writeStoredChatCache(ADMIN_CHAT_CACHE_KEY, { conversations: list, selectedConversation: next });
                return next;
            });
        } catch (err) {
            if (!isServiceUnavailableError(err)) {
                console.error("Failed to load conversations:", err);
            }
        }
    }, []);

    const fetchDirectory = useCallback(async () => {
        try {
            const res = await api.get("/api/admin/chat/directory");
            const list = Array.isArray(res.data) ? res.data : [];
            setDirectory(list);
            writeStoredChatCache(ADMIN_CHAT_CACHE_KEY, { directory: list });
        } catch (err) {
            if (!isServiceUnavailableError(err)) {
                console.error("Failed to load chat directory:", err);
            }
        }
    }, []);

    const fetchMessages = useCallback(async (conversation, options = {}) => {
        if (!conversation?._id) return;
        const conversationId = conversation._id;
        const cachedMessages = messageCacheRef.current[conversationId];
        const silent = Boolean(options.silent);
        const refreshConversations = options.refreshConversations !== false;
        const markRead = options.markRead !== false;
        if (cachedMessages) {
            setMessages(cachedMessages);
        }
        if (!silent && !cachedMessages) setLoading(true);
        try {
            const res = await api.get(`/api/admin/chat/conversations/${conversationId}/messages`, {
                cache: false,
                params: { limit: 50, markRead },
            });
            const serverList = Array.isArray(res.data) ? res.data : [];
            const list = mergeLocalSendingMessages(serverList, messageCacheRef.current[conversationId] || []);
            messageCacheRef.current[conversationId] = list;
            if (selectedConversationIdRef.current === conversationId) {
                setMessages(list);
            }
            writeStoredChatCache(ADMIN_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
            if (refreshConversations) fetchConversations();
        } catch (err) {
            console.error("Failed to load chat messages:", err);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [fetchConversations]);

    const prefetchMessages = useCallback(async (conversation) => {
        if (!conversation?._id || messageCacheRef.current[conversation._id]) return;
        try {
            const res = await api.get(`/api/admin/chat/conversations/${conversation._id}/messages`, {
                cache: false,
                params: { limit: 50, markRead: false },
            });
            messageCacheRef.current[conversation._id] = Array.isArray(res.data) ? res.data : [];
            writeStoredChatCache(ADMIN_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
        } catch (err) {
            console.error("Failed to prefetch chat messages:", err);
        }
    }, []);

    useEffect(() => {
        fetchConversations();
        const intervalMs = open ? 5000 : 10000;
        const id = window.setInterval(() => {
            if (!document.hidden) fetchConversations();
        }, intervalMs);
        return () => window.clearInterval(id);
    }, [fetchConversations, open]);

    useEffect(() => {
        const id = window.setTimeout(() => {
            if (!document.hidden) fetchDirectory();
        }, 1500);
        return () => window.clearTimeout(id);
    }, [fetchDirectory]);

    useEffect(() => {
        if (open && directory.length === 0) fetchDirectory();
    }, [open, directory.length, fetchDirectory]);

    useEffect(() => {
        if (!conversations.length) return;
        const selected = selectedConversation || visibleConversations[0] || conversations[0];
        prefetchMessages(selected);
    }, [conversations, selectedConversation, visibleConversations, prefetchMessages]);

    useEffect(() => {
        if (!visibleConversations.length) {
            setSelectedConversation(null);
            setMessages([]);
            return;
        }
        if (selectedIsVisible) return;
        const next = visibleConversations[0];
        setSelectedConversation(next);
        setMessages(messageCacheRef.current[next._id] || []);
        writeStoredChatCache(ADMIN_CHAT_CACHE_KEY, { selectedConversation: next });
    }, [chatView, visibleConversations, selectedIsVisible]);

    useEffect(() => {
        if (!open) return undefined;
        fetchMessages(selectedConversation);
        const id = window.setInterval(
            () => fetchMessages(selectedConversation, { silent: true, refreshConversations: false }),
            3000,
        );
        return () => window.clearInterval(id);
    }, [open, selectedConversation, fetchMessages]);

    useEffect(() => {
        const handleClick = (event) => {
            const clickedButton = wrapRef.current?.contains(event.target);
            const clickedPanel = panelRef.current?.contains(event.target);
            if (!clickedButton && !clickedPanel) setOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const sendMessage = async (event) => {
        event.preventDefault();
        if (chatView === "monitor") return;
        const text = draft.trim();
        const attachmentPayload = attachment;
        if ((!text && !attachmentPayload) || !selectedConversation?._id) return;
        const conversationId = selectedConversation._id;
        const optimisticMessage = {
            _id: `temp-${Date.now()}`,
            senderModel: "Admin",
            senderName: "Admin",
            sender: { _id: "admin" },
            message: text,
            attachment: attachmentPayload,
            readStatus: "unread",
            createdAt: new Date().toISOString(),
            pending: true,
        };
        const currentMessages = messageCacheRef.current[conversationId] || messages;
        const optimisticMessages = [...currentMessages, optimisticMessage];
        messageCacheRef.current[conversationId] = optimisticMessages;
        writeStoredChatCache(ADMIN_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
        setMessages(optimisticMessages);
        setDraft("");
        setAttachment(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        try {
            const uploadedAttachment = attachmentPayload?.uploadKey
                ? await attachmentUploadRef.current[attachmentPayload.uploadKey]
                : attachmentPayload;
            const serverAttachmentPayload = uploadedAttachment?.url && !String(uploadedAttachment.url).startsWith("blob:")
                ? {
                    name: uploadedAttachment.name,
                    type: uploadedAttachment.type,
                    size: uploadedAttachment.size,
                    storagePath: uploadedAttachment.storagePath,
                    fileKey: uploadedAttachment.fileKey,
                    url: uploadedAttachment.url,
                }
                : uploadedAttachment;
            const res = await api.post(
                `/api/admin/chat/conversations/${conversationId}/messages`,
                { message: text, attachment: serverAttachmentPayload },
                { skipSaveToast: true },
            );
            const nextMessages = (messageCacheRef.current[conversationId] || optimisticMessages)
                .map((message) => message._id === optimisticMessage._id ? res.data : message);
            messageCacheRef.current[conversationId] = nextMessages;
            writeStoredChatCache(ADMIN_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
            setMessages(nextMessages);
            fetchConversations();
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to send message.";
            const failedMessages = (messageCacheRef.current[conversationId] || optimisticMessages)
                .map((message) => message._id === optimisticMessage._id ? { ...message, pending: false, failed: true, errorMessage } : message);
            messageCacheRef.current[conversationId] = failedMessages;
            writeStoredChatCache(ADMIN_CHAT_CACHE_KEY, { messagesByConversation: messageCacheRef.current });
            setMessages(failedMessages);
            setDraft(text);
            setAttachment(attachmentPayload);
            console.error("Failed to send chat message:", err.response?.data || err);
            alert(errorMessage);
        }
    };

    const handleAttachmentChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        attachChatFile(file, event);
    };

    const attachChatFile = (file, event) => {
        if (file.size > 200 * 1024 * 1024) {
            alert("Attachment must be less than 200MB.");
            if (event?.target) event.target.value = "";
            return;
        }
        const uploadKey = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const localUrl = URL.createObjectURL(file);
        const pendingAttachment = {
            name: file.name,
            type: file.type || "application/octet-stream",
            size: file.size,
            localUrl,
            uploadKey,
            uploading: true,
        };
        setAttachment(pendingAttachment);
        setAttachmentLoading(true);
        const uploadPromise = uploadChatFile(file);
        attachmentUploadRef.current[uploadKey] = uploadPromise;
        uploadPromise.then((uploaded) => {
            setAttachment((current) => current?.uploadKey === uploadKey ? uploaded : current);
        }).catch((err) => {
            setAttachment((current) => current?.uploadKey === uploadKey ? null : current);
            if (event?.target) event.target.value = "";
            if (attachmentUploadRef.current[uploadKey]) {
                alert(err.message || "File upload failed. Please try again.");
            }
        }).finally(() => {
            delete attachmentUploadRef.current[uploadKey];
            setAttachmentLoading(false);
        });
    };

    const handlePasteAttachment = (event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const fileItem = items.find((item) => item.kind === "file");
        const file = fileItem?.getAsFile();
        if (!file) return;

        event.preventDefault();
        const extension = file.type?.split("/")?.[1] || "png";
        const namedFile = new File(
            [file],
            file.name || `Screenshot-${new Date().toISOString().replace(/[:.]/g, "-")}.${extension}`,
            { type: file.type || "image/png" },
        );
        attachChatFile(namedFile);
    };

    const startConversation = async (user) => {
        try {
            setEmployeePickerOpen(false);
            const res = await api.post(
                "/api/admin/chat/conversations",
                { targetId: user._id, targetModel: user.userModel || "Employee" },
                { skipSaveToast: true },
            );
            setSelectedConversation(res.data);
            setMobileTab("chat");
            await fetchConversations();
            await fetchMessages(res.data, { silent: true });
        } catch (err) {
            console.error("Failed to start conversation:", err);
        }
    };

    const inExportRange = (message) => {
        const created = new Date(message.createdAt);
        if (exportFrom) {
            const from = new Date(`${exportFrom}T00:00:00`);
            if (created < from) return false;
        }
        if (exportTo) {
            const to = new Date(`${exportTo}T23:59:59`);
            if (created > to) return false;
        }
        return true;
    };

    const createPdfDocument = async () => {
        const jsPdfModule = await import("jspdf");
        await import("jspdf/dist/jspdf.umd.min.js");
        const browserWindow = typeof window !== "undefined" ? window : {};
        const candidates = [
            jsPdfModule?.jsPDF,
            jsPdfModule?.default,
            jsPdfModule?.default?.jsPDF,
            browserWindow?.jspdf?.jsPDF,
            browserWindow?.jsPDF,
        ].filter((candidate) => typeof candidate === "function");

        for (const Candidate of candidates) {
            try {
                return new Candidate({ unit: "pt", format: "a4" });
            } catch (error) {
                console.warn("Skipping unavailable PDF constructor:", error);
            }
        }

        return null;
    };

    const downloadPdf = async (filename, records, title) => {
        const doc = await createPdfDocument();
        if (!doc) {
            throw new Error("PDF exporter is not available. Please refresh the page and try again.");
        }
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 34;
        const maxWidth = pageWidth - margin * 2;
        let y = margin;

        const drawPageShell = () => {
            doc.setFillColor(248, 250, 252);
            doc.rect(0, 0, pageWidth, pageHeight, "F");
            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.7);
            doc.line(margin, pageHeight - 26, pageWidth - margin, pageHeight - 26);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            doc.text("Generated from admin chat monitor", margin, pageHeight - 13);
        };
        const addPageIfNeeded = (height = 24) => {
            if (y + height <= pageHeight - margin) return;
            doc.addPage();
            drawPageShell();
            y = margin;
        };
        const cleanPdfText = (value) => String(value || "")
            .replace(/[^\x20-\x7E\n]/g, " ")
            .replace(/\s+/g, " ")
            .trim();
        const fitPdfText = (value, width) => {
            const text = cleanPdfText(value);
            if (!text || doc.getTextWidth(text) <= width) return text;
            if (width <= doc.getTextWidth("...")) return "...";
            let start = Math.ceil(text.length * 0.6);
            let end = Math.floor(text.length * 0.25);
            let fitted = `${text.slice(0, start)}...${text.slice(text.length - end)}`;
            while (fitted.length > 8 && doc.getTextWidth(fitted) > width) {
                if (start > end) start -= 1;
                else end -= 1;
                fitted = `${text.slice(0, Math.max(start, 3))}...${text.slice(text.length - Math.max(end, 3))}`;
            }
            return fitted;
        };
        const drawPill = (text, x, lineY, width) => {
            doc.setFillColor(241, 245, 249);
            doc.roundedRect(x, lineY - 8, width, 16, 6, 6, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            doc.setTextColor(71, 85, 105);
            doc.text(cleanPdfText(text), x + 9, lineY + 3);
        };
        const drawMessageBubble = (record) => {
            const sender = cleanPdfText(record.sender || "Unknown");
            const messageText = cleanPdfText(record.message || (record.attachment ? "Attachment" : ""));
            const attachmentText = cleanPdfText(record.attachment || "");
            const isRight = record.employee2 && sender.toLowerCase() === cleanPdfText(record.employee2).toLowerCase();
            const meta = `${sender}  |  ${record.date} ${record.time}`;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            const messageWidth = Math.min(doc.getTextWidth(messageText || "Attachment") + 28, 350);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            const metaWidth = doc.getTextWidth(meta) + 28;
            const maxAttachmentTextWidth = 290;
            const fittedAttachmentText = attachmentText ? fitPdfText(`File: ${attachmentText}`, maxAttachmentTextWidth) : "";
            const attachmentWidth = fittedAttachmentText ? Math.min(doc.getTextWidth(fittedAttachmentText) + 42, 330) : 0;
            const bubbleWidth = Math.min(
                Math.max(145, messageWidth, metaWidth, attachmentWidth),
                maxWidth - 56,
            );
            const x = isRight ? pageWidth - margin - bubbleWidth : margin;
            const messageLines = doc.splitTextToSize(messageText || "Attachment", bubbleWidth - 22);
            const hasAttachment = Boolean(fittedAttachmentText);
            const bubbleHeight = 24 + (messageLines.length * 11) + (hasAttachment ? 24 : 0);

            addPageIfNeeded(bubbleHeight + 8);

            doc.setFillColor(isRight ? 229 : 255, isRight ? 231 : 255, isRight ? 235 : 255);
            doc.setDrawColor(isRight ? 203 : 226, isRight ? 213 : 232, isRight ? 225 : 240);
            doc.roundedRect(x, y, bubbleWidth, bubbleHeight, 8, 8, "FD");

            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            doc.setTextColor(71, 85, 105);
            doc.text(meta, x + 11, y + 13);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(15, 23, 42);
            doc.text(messageLines, x + 11, y + 27);

            if (hasAttachment) {
                const fileY = y + 29 + messageLines.length * 11;
                doc.setFillColor(241, 245, 249);
                doc.roundedRect(x + 11, fileY, bubbleWidth - 22, 18, 6, 6, "F");
                doc.setFont("helvetica", "bold");
                doc.setFontSize(8);
                doc.setTextColor(71, 85, 105);
                doc.text(fitPdfText(fittedAttachmentText, bubbleWidth - 40), x + 20, fileY + 12);
            }

            y += bubbleHeight + 6;
        };

        drawPageShell();
        doc.setFillColor(241, 245, 249);
        doc.setDrawColor(203, 213, 225);
        doc.roundedRect(margin, y, maxWidth, 58, 8, 8, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(15, 23, 42);
        doc.text(cleanPdfText(title), margin + 14, y + 21);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        const chatName = records[0]?.conversation || "Employee chat";
        doc.text(cleanPdfText(`Chat: ${chatName}`), margin + 14, y + 38);

        const rangeText = exportFrom || exportTo
            ? `Range: ${exportFrom || "Start"} to ${exportTo || "Today"}`
            : "Range: All dates";
        doc.text(cleanPdfText(rangeText), margin + 14, y + 51);
        y += 72;

        drawPill(`${records.length} messages`, margin, y, 86);
        drawPill("Monitor export", margin + 94, y, 94);
        y += 22;

        records.forEach((record) => {
            drawMessageBubble(record);
        });

        doc.save(filename);
    };

    const loadExportMessages = async (conversation) => {
        const res = await api.get(`/api/admin/chat/conversations/${conversation._id}/messages`, {
            cache: false,
            params: { limit: 5000, markRead: false },
        });
        return Array.isArray(res.data) ? res.data : [];
    };

    const exportMonitorChats = async () => {
        if (!selectedConversation) {
            alert("Select an employee chat to export.");
            return;
        }

        setExporting(true);
        try {
            const records = [];
            const participants = selectedConversation.participants || [];
            const exportedMessages = (await loadExportMessages(selectedConversation)).filter(inExportRange);
            exportedMessages.forEach((message) => {
                const created = new Date(message.createdAt);
                records.push({
                    conversation: participantLabel(selectedConversation),
                    employee1: participantDisplayName(participants[0]) || "",
                    employee2: participantDisplayName(participants[1]) || "",
                    sender: message.senderName || message.sender?.name || message.senderModel,
                    message: messagePreview(message),
                    attachment: message.attachment?.name || message.attachment?.url || message.originalAttachment?.name || message.originalAttachment?.url || "",
                    date: created.toLocaleDateString("en-IN"),
                    time: created.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" }),
                });
            });

            if (!records.length) {
                alert("No messages found for the selected export range.");
                return;
            }

            const stamp = new Date().toISOString().slice(0, 10);
            const range = exportFrom || exportTo ? `_${exportFrom || "start"}_to_${exportTo || "today"}` : "";
            await downloadPdf(
                `employee_monitor_chat_custom_range_${stamp}${range}.pdf`,
                records,
                "Custom Range Employee Chat Download",
            );
        } catch (err) {
            console.error("Failed to export monitoring chats:", err);
            alert(err.response?.data?.message || err.message || "Failed to export monitoring chats.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div ref={wrapRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                className="group relative h-11 w-11 rounded-full border border-[var(--accent-soft)] bg-[var(--accent-primary)] text-white shadow-lg shadow-[var(--accent-primary)]/30 hover:-translate-y-0.5 hover:border-[var(--accent-primary)] hover:bg-[var(--accent-primary)] hover:shadow-[var(--accent-primary)]/45 dark:border-[var(--accent-primary)]/50 dark:bg-[var(--accent-primary)] dark:text-white dark:hover:bg-[var(--accent-hover)] flex items-center justify-center transition-all"
                title="Chat"
            >
                <MessageCircle size={19} strokeWidth={2.4} />
                {unreadTotal > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-black leading-none flex items-center justify-center ring-2 ring-white dark:ring-[#111111] shadow-sm">
                        {unreadTotal > 9 ? "9+" : unreadTotal}
                    </span>
                )}
            </button>

            {open && createPortal(
                <div ref={panelRef} className="md:fixed md:top-[64px] md:right-4 md:left-auto md:w-[560px] md:h-[620px] md:max-h-[calc(100vh-120px)] fixed inset-x-2 top-[60px] bottom-2 h-auto max-h-[calc(100vh-80px)] w-auto bg-[var(--bg-secondary)] dark:bg-neutral-950 border border-slate-200 dark:border-neutral-800 rounded-2xl shadow-2xl z-[125] overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-slate-200 bg-[var(--card-bg)] dark:border-neutral-800 dark:bg-neutral-950 shrink-0 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {mobileTab === "chat" && (
                                <button
                                    type="button"
                                    onClick={() => setMobileTab("list")}
                                    className="md:hidden p-1 mr-1 rounded-lg hover:bg-black/5 dark:hover:bg-[var(--card-bg)]/5 text-slate-500"
                                >
                                    &larr; Chats
                                </button>
                            )}
                            <div>
                                <p className="text-xs font-semibold uppercase text-[var(--accent-primary)]">Chat</p>
                                <div className="flex items-center gap-3">
                                    <h3 className="font-semibold">{chatView === "inbox" ? "Admin inbox" : "Employee monitor"}</h3>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className={`rounded-xl border border-slate-200 bg-slate-100 p-1 text-[11px] font-semibold dark:border-neutral-800 dark:bg-neutral-900 ${mobileTab === 'chat' ? 'hidden md:inline-flex' : 'inline-flex'}`}>
                                <button
                                    type="button"
                                    onClick={() => setChatView("inbox")}
                                    className={`px-3 py-1.5 rounded-lg transition ${chatView === "inbox" ? "bg-[var(--accent-primary)] text-white shadow-sm" : "text-slate-500 hover:text-slate-800 dark:text-neutral-400 dark:hover:text-neutral-100"}`}
                                >
                                    Admin ({inboxConversations.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setChatView("monitor")}
                                    className={`px-3 py-1.5 rounded-lg transition ${chatView === "monitor" ? "bg-[var(--accent-primary)] text-white shadow-sm" : "text-slate-500 hover:text-slate-800 dark:text-neutral-400 dark:hover:text-neutral-100"}`}
                                >
                                    Monitor ({monitorConversations.length})
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="md:hidden p-2 rounded-xl border border-slate-200 dark:border-neutral-800 text-slate-500 hover:text-slate-800 dark:text-neutral-400 dark:hover:text-neutral-100"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[210px_minmax(0,1fr)] flex-1 min-h-0 overflow-hidden">
                        <div className={`border-r border-slate-200 bg-[var(--card-bg)] overflow-y-auto dark:border-neutral-800 dark:bg-neutral-950 ${mobileTab === "chat" ? "hidden md:block" : "block"}`}>
                            <div className="sticky top-0 z-10 border-b border-slate-200 bg-[var(--card-bg)] p-2 dark:border-neutral-800 dark:bg-neutral-950">
                                <div className="flex h-9 items-center gap-2 rounded-xl border border-slate-200 bg-[var(--bg-secondary)] px-3 dark:border-neutral-800 dark:bg-neutral-900">
                                    <Search size={14} className="shrink-0 text-slate-400" />
                                    <input
                                        value={chatSearch}
                                        onChange={(event) => setChatSearch(event.target.value)}
                                        placeholder="Search employee"
                                        className="min-w-0 flex-1 bg-transparent text-xs font-semibold outline-none placeholder:text-slate-400"
                                    />
                                    {chatSearch && (
                                        <button
                                            type="button"
                                            onClick={() => setChatSearch("")}
                                            className="text-slate-400 hover:text-slate-700 dark:hover:text-neutral-100"
                                            aria-label="Clear chat search"
                                        >
                                            <X size={13} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            {chatView === "inbox" && (
                            <div className="relative p-2 border-b border-[var(--border-color)]">
                                <p className="text-[10px] font-semibold uppercase text-[var(--text-secondary)] mb-2">Start chat</p>
                                <button
                                    type="button"
                                    onClick={() => setEmployeePickerOpen((current) => !current)}
                                    className="flex h-9 w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-[var(--bg-secondary)] px-3 text-left text-xs font-semibold text-slate-700 transition hover:border-[var(--accent-soft)] hover:bg-[var(--accent-light)] dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200"
                                >
                                    <span className="truncate">Select Contact</span>
                                    <span className={`text-slate-400 transition ${employeePickerOpen ? "rotate-180" : ""}`}>⌄</span>
                                </button>

                                {employeePickerOpen && (
                                    <div className="absolute left-2 right-2 top-[70px] z-20 max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-[var(--card-bg)] p-1 shadow-xl dark:border-neutral-800 dark:bg-neutral-950">
                                        {filteredDirectory.length === 0 ? (
                                            <p className="px-3 py-2 text-xs text-[var(--text-secondary)]">No contacts found</p>
                                        ) : (
                                            filteredDirectory.map((user, index) => (
                                                <button
                                                    key={user._id || user.id || user.email || `directory-user-${index}`}
                                                    type="button"
                                                    onClick={() => startConversation(user)}
                                                    className="w-full rounded-lg px-3 py-2 text-left text-xs transition hover:bg-[var(--accent-light)] hover:text-[var(--accent-primary)] dark:hover:bg-neutral-900"
                                                >
                                                    <div className="flex items-center justify-between gap-1">
                                                        <span className="truncate font-semibold">{user.name}</span>
                                                        {user.userModel === 'Admin' && (
                                                            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-bold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                                                {user.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="block truncate text-[10px] text-[var(--text-secondary)]">{user.team || user.employeeId || user.email}</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                            )}
                            {!filteredVisibleConversations.length && (
                                <p className="p-4 text-xs text-[var(--text-secondary)]">
                                    {normalizedChatSearch ? "No chats match your search" : chatView === "inbox" ? "No admin messages yet" : "No employee chats to monitor"}
                                </p>
                            )}
                            {filteredVisibleConversations.map((conversation, index) => (
                                <button
                                    key={conversation._id || conversation.id || `conversation-${index}`}
                                    type="button"
                                    onClick={() => {
                                        setSelectedConversation(conversation);
                                        setMessages(messageCacheRef.current[conversation._id] || []);
                                        setLoading(!messageCacheRef.current[conversation._id]);
                                        writeStoredChatCache(ADMIN_CHAT_CACHE_KEY, { selectedConversation: conversation });
                                        setMobileTab("chat");
                                    }}
                                    className={`w-full text-left p-3 border-b border-slate-100 transition dark:border-neutral-900 ${selectedConversation?._id === conversation._id ? "bg-slate-100 dark:bg-neutral-900" : "hover:bg-[var(--bg-secondary)] dark:hover:bg-neutral-900/70"}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <p className="text-xs font-semibold truncate">{participantLabel(conversation)}</p>
                                            {isSuperAdminConversation(conversation) && (
                                                <span className="shrink-0 rounded bg-purple-100 px-1.5 py-0.5 text-[9px] font-bold text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
                                                    SUPER ADMIN
                                                </span>
                                            )}
                                        </div>
                                        {conversation.unreadCount > 0 && <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />}
                                    </div>
                                    <p className="text-[9px] font-semibold uppercase text-[var(--accent-primary)]">
                                        {isSuperAdminConversation(conversation) ? "Super Admin Direct" : isAdminConversation(conversation) ? "Admin chat" : "Monitor only"}
                                    </p>
                                    <p className="text-[10px] text-[var(--text-secondary)] truncate">
                                        {conversationPreview(conversation, messageCacheRef.current[conversation._id] || [])}
                                    </p>
                                </button>
                            ))}
                        </div>
                        <div className={`flex min-h-0 flex-col bg-[var(--bg-secondary)] dark:bg-neutral-950 ${mobileTab === "list" ? "hidden md:flex" : "flex"}`}>
                            {chatView === "monitor" && selectedConversation && (
                                <div className="shrink-0 border-b border-slate-200 bg-[var(--card-bg)] px-3 py-2 dark:border-neutral-800 dark:bg-neutral-950">
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-semibold uppercase text-[var(--accent-primary)]">Chat export</p>
                                            <h4 className="truncate text-xs font-semibold">{participantLabel(selectedConversation)}</h4>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={exporting || !selectedConversation}
                                            onClick={exportMonitorChats}
                                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--accent-primary)] text-white transition hover:bg-[var(--accent-hover)] disabled:opacity-50"
                                            title="Download custom range PDF"
                                        >
                                            <Download size={16} />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <input
                                            type="date"
                                            value={exportFrom}
                                            onChange={(event) => setExportFrom(event.target.value)}
                                            className="h-8 rounded-lg border border-slate-200 bg-[var(--bg-secondary)] px-2 text-[11px] outline-none focus:border-blue-400 dark:border-neutral-800 dark:bg-neutral-900"
                                            title="Export from"
                                        />
                                        <input
                                            type="date"
                                            value={exportTo}
                                            onChange={(event) => setExportTo(event.target.value)}
                                            className="h-8 rounded-lg border border-slate-200 bg-[var(--bg-secondary)] px-2 text-[11px] outline-none focus:border-blue-400 dark:border-neutral-800 dark:bg-neutral-900"
                                            title="Export to"
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-2.5">
                                {!loading && messages.length === 0 && (
                                    <p className="text-xs text-[var(--text-secondary)] text-center mt-10">No messages yet</p>
                                )}
                                {messages.map((message, index) => {
                                    const showDateSeparator = index === 0
                                        || chatDateKey(message.createdAt) !== chatDateKey(messages[index - 1]?.createdAt);
                                    const senderId = typeof message.sender === "object" ? message.sender?._id : message.sender;
                                    const monitorParticipants = selectedConversation?.participants || [];
                                    const monitorRightParticipant = monitorParticipants[1];
                                    const senderParticipant = monitorParticipants.find((participant) =>
                                        senderId?.toString() === participant?.id?.toString()
                                            && message.senderModel === participant?.model,
                                    );
                                    const monitorRight = chatView === "monitor"
                                        && senderId?.toString() === monitorRightParticipant?.id?.toString()
                                        && message.senderModel === monitorRightParticipant?.model;
                                    const mine = chatView === "monitor" ? monitorRight : message.senderModel === "Admin";
                                    const senderName = isGenericParticipantName(message.senderName || message.sender?.name)
                                        ? participantDisplayName(senderParticipant || { model: message.senderModel, id: senderId, missingUser: true })
                                        : (message.senderName || message.sender?.name || message.senderModel);
                                    const visibleMessage = chatView === "monitor" && message.deletedForEveryone
                                        ? (message.originalMessage || message.message)
                                        : message.message;
                                    const visibleAttachment = chatView === "monitor" && message.deletedForEveryone
                                        ? (message.originalAttachment || message.attachment)
                                        : message.attachment;
                                    const missingOriginalUnsent = chatView === "monitor"
                                        && message.deletedForEveryone
                                        && !visibleMessage
                                        && !(visibleAttachment?.url || visibleAttachment?.dataUrl);
                                    return (
                                        <div key={message._id || message.id || `message-${index}`}>
                                            {showDateSeparator && (
                                                <div className="my-3 flex items-center gap-2" role="separator" aria-label={chatDateLabel(message.createdAt)}>
                                                    <span className="h-px flex-1 bg-slate-200 dark:bg-neutral-800" />
                                                    <span className="rounded-full border border-slate-200 bg-[var(--card-bg)] px-3 py-1 text-[10px] font-semibold text-slate-500 shadow-sm dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
                                                        {chatDateLabel(message.createdAt)}
                                                    </span>
                                                    <span className="h-px flex-1 bg-slate-200 dark:bg-neutral-800" />
                                                </div>
                                            )}
                                            <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-xs shadow-sm ${mine ? "rounded-br-md bg-[var(--accent-primary)] text-white" : "rounded-bl-md bg-[var(--card-bg)] text-slate-900 border border-slate-200 dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-800"}`}>
                                                {chatView === "monitor" && (
                                                    <p className={`mb-1 text-[10px] font-semibold uppercase ${mine ? "text-white/80" : "text-[var(--accent-primary)]"}`}>
                                                        {senderName}
                                                    </p>
                                                )}
                                                {message.deletedForEveryone && chatView === "monitor" && (
                                                    <p className={`mb-1 inline-flex rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase ${mine ? "bg-[var(--card-bg)]/15 text-white" : "bg-red-50 text-red-500 dark:bg-red-950/30 dark:text-red-300"}`}>
                                                        Unsent by employee
                                                    </p>
                                                )}
                                                {missingOriginalUnsent ? (
                                                    <p className={`italic ${mine ? "text-white/80" : "text-[var(--text-secondary)]"}`}>
                                                        Original content unavailable. This message was unsent before retention was enabled.
                                                    </p>
                                                ) : (
                                                    visibleMessage
                                                        ? <p className="whitespace-pre-wrap break-words leading-snug">{visibleMessage}</p>
                                                        : !(visibleAttachment?.url || visibleAttachment?.dataUrl || visibleAttachment?.localUrl) && (
                                                            <p className={`italic ${mine ? "text-white/80" : "text-[var(--text-secondary)]"}`}>
                                                                Message unavailable
                                                            </p>
                                                        )
                                                )}
                                                {visibleAttachment && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (!(visibleAttachment?.url || visibleAttachment?.dataUrl || visibleAttachment?.localUrl)) return;
                                                            setPreviewAttachment(visibleAttachment);
                                                            setPreviewFullScreen(false);
                                                        }}
                                                        disabled={!(visibleAttachment?.url || visibleAttachment?.dataUrl || visibleAttachment?.localUrl)}
                                                        className={`mt-2 flex items-center gap-2 rounded-xl px-2 py-2 font-semibold disabled:cursor-not-allowed disabled:opacity-70 ${mine ? "bg-[var(--card-bg)]/15 text-white" : "bg-slate-100 text-[var(--accent-primary)] dark:bg-neutral-800 dark:text-[var(--heading-secondary)]"}`}
                                                    >
                                                        <Paperclip size={13} />
                                                        <span className="truncate">
                                                            {visibleAttachment.name || "Attachment"}
                                                            {!(visibleAttachment?.url || visibleAttachment?.dataUrl || visibleAttachment?.localUrl) ? " unavailable" : ""}
                                                        </span>
                                                    </button>
                                                )}
                                                <div className={`mt-1 flex items-center gap-1 ${mine ? "justify-end text-white/75" : "justify-start text-[var(--text-secondary)]"}`}>
                                                    <span className="text-[9px]">
                                                        {new Date(message.createdAt).toLocaleTimeString("en-IN", { timeZone: CHAT_TIME_ZONE, hour: "2-digit", minute: "2-digit" })}
                                                    </span>
                                                    {message.pending && <span className="text-[9px]">Sending</span>}
                                                    {message.failed && <span className="text-[9px] text-red-200" title={message.errorMessage}>Failed</span>}
                                                    {mine && (message.readStatus === "read" ? <CheckCheck size={12} /> : <Check size={12} />)}
                                                </div>
                                            </div>
                                        </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <form onSubmit={sendMessage} className="shrink-0 p-3 border-t border-slate-200 bg-[var(--card-bg)] dark:border-neutral-800 dark:bg-neutral-950">
                                {chatView === "monitor" && (
                                    <div className="mb-2 rounded-xl bg-[var(--accent-light)] border border-[var(--border-color)] px-3 py-2 text-[11px] font-semibold text-[var(--heading-secondary)] dark:bg-[var(--accent-subtle)] dark:border-[var(--border-color)]/30 dark:text-[var(--heading-secondary)]">
                                        Monitoring view only. Admin viewing does not mark employee messages as read.
                                    </div>
                                )}
                                {(attachment || attachmentLoading) && (
                                    <div className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-[var(--bg-secondary)] px-3 py-2 text-xs">
                                        <span className="min-w-0 truncate font-semibold">{attachment?.uploading ? `${attachment.name} - uploading` : attachmentLoading ? "Attaching..." : attachment.name}</span>
                                        {!attachmentLoading && (
                                            <button type="button" onClick={() => setAttachment(null)} className="font-black text-red-500">Remove</button>
                                        )}
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleAttachmentChange} />
                                    <button type="button" disabled={chatView === "monitor"} onClick={() => fileInputRef.current?.click()} className="w-10 h-10 shrink-0 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] flex items-center justify-center disabled:opacity-50">
                                        <Paperclip size={16} />
                                    </button>
                                    <input
                                        value={draft}
                                        onChange={(event) => setDraft(event.target.value)}
                                        onPaste={handlePasteAttachment}
                                        disabled={!selectedConversation || chatView === "monitor"}
                                        placeholder={chatView === "monitor" ? "Monitor only" : selectedConversation ? `Message ${selectedConversation.title}` : "Select chat"}
                                        className="min-w-0 flex-1 h-10 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm outline-none"
                                    />
                                    <button type="submit" disabled={chatView === "monitor"} className="w-10 h-10 shrink-0 rounded-xl bg-[var(--accent-primary)] text-white flex items-center justify-center disabled:opacity-50">
                                        <Send size={16} />
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {previewAttachment && createPortal(
                <div className="fixed inset-0 z-[240] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                    <div className={`m-auto flex flex-col overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl ${previewFullScreen ? "h-[90vh] w-[92vw] max-w-none" : "max-h-[88vh] w-full max-w-4xl"}`}>
                        <div className="flex items-center justify-between gap-3 border-b border-[var(--border-color)] px-4 py-3">
                            <div className="min-w-0">
                                <p className="text-xs font-bold uppercase text-[var(--accent-primary)]">File preview</p>
                                <h3 className="truncate text-sm font-bold">{previewAttachment.name || "Attachment"}</h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setPreviewAttachment(null);
                                    setPreviewFullScreen(false);
                                }}
                                className="flex h-9 w-9 items-center justify-center rounded-xl hover:bg-[var(--bg-secondary)]"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="flex min-h-[320px] flex-1 items-center justify-center overflow-auto bg-[var(--bg-primary)] p-4">
                            {isImageAttachment(previewAttachment) ? (
                                previewFileLoading ? (
                                    <p className="text-sm font-bold text-[var(--text-secondary)]">Opening image...</p>
                                ) : previewFileError ? (
                                    <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-red-200 bg-red-50 p-8 text-center text-red-600">
                                        <Paperclip size={28} className="mb-3" />
                                        <p className="text-sm font-bold">{previewFileError}</p>
                                    </div>
                                ) : (
                                    <img src={previewFileUrl} alt={previewAttachment.name || "Attachment"} className={`mx-auto max-w-full rounded-xl object-contain ${previewFullScreen ? "max-h-[76vh]" : "max-h-[68vh]"}`} />
                                )
                            ) : isPdfAttachment(previewAttachment) ? (
                                previewFileLoading ? (
                                    <p className="text-sm font-bold text-[var(--text-secondary)]">Opening PDF...</p>
                                ) : previewFileError ? (
                                    <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-red-200 bg-red-50 p-8 text-center text-red-600">
                                        <Paperclip size={28} className="mb-3" />
                                        <p className="text-sm font-bold">{previewFileError}</p>
                                    </div>
                                ) : (
                                    <iframe title={previewAttachment.name || "PDF preview"} src={previewFileUrl} className={`w-full rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] ${previewFullScreen ? "h-[76vh]" : "h-[68vh]"}`} />
                                )
                            ) : isExcelAttachment(previewAttachment) ? (
                                previewFileLoading ? (
                                    <p className="text-sm font-bold text-[var(--text-secondary)]">Opening Excel sheet...</p>
                                ) : previewFileError ? (
                                    <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-red-200 bg-red-50 p-8 text-center text-red-600">
                                        <FileSpreadsheet size={34} className="mb-3" />
                                        <p className="text-sm font-bold">{previewFileError}</p>
                                    </div>
                                ) : previewSheet?.rows?.length ? (
                                    <div className="h-full w-full overflow-auto rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)]">
                                        <div className="sticky top-0 z-10 border-b border-slate-200 bg-[var(--bg-secondary)] px-3 py-2 text-xs font-bold text-slate-700">
                                            {previewSheet.name}{previewSheet.totalRows > previewSheet.rows.length ? ` - showing first ${previewSheet.rows.length} rows` : ""}
                                        </div>
                                        <table className="min-w-full border-collapse text-left text-xs text-slate-900">
                                            <tbody>
                                                {previewSheet.rows.map((row, rowIndex) => (
                                                    <tr key={rowIndex} className={rowIndex === 0 ? "bg-slate-100 font-bold" : ""}>
                                                        {row.map((cell, cellIndex) => (
                                                            <td key={cellIndex} className="max-w-[220px] border border-slate-200 px-3 py-2 align-top">
                                                                <span className="block truncate">{String(cell ?? "")}</span>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center">
                                        <FileSpreadsheet size={34} className="mb-3 text-emerald-500" />
                                        <p className="text-sm font-bold">{previewAttachment.name || "Excel sheet"}</p>
                                        <p className="mt-1 text-xs text-[var(--text-secondary)]">No sheet data found.</p>
                                    </div>
                                )
                            ) : (
                                <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center">
                                    <Paperclip size={28} className="mb-3 text-[var(--accent-primary)]" />
                                    <p className="text-sm font-bold">{previewAttachment.name || "Attachment"}</p>
                                    <p className="mt-1 text-xs text-[var(--text-secondary)]">Preview is not available for this file type inside the app.</p>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center justify-end gap-2 border-t border-[var(--border-color)] px-4 py-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setPreviewAttachment(null);
                                    setPreviewFullScreen(false);
                                }}
                                className="rounded-xl bg-[var(--bg-secondary)] px-4 py-2 text-xs font-bold text-[var(--text-primary)]"
                            >
                                Cancel
                            </button>
                            <button type="button" onClick={() => setPreviewFullScreen((current) => !current)} className="rounded-xl border border-[var(--border-color)] px-4 py-2 text-xs font-bold text-[var(--text-primary)]">
                                {previewFullScreen ? "Exit full view" : "Full view"}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    downloadChatAttachment(previewAttachment, "admin_token").catch((error) => {
                                        alert(error.message || "Could not download this file.");
                                    });
                                }}
                                className="inline-flex items-center gap-2 rounded-xl bg-[var(--accent-primary)] px-4 py-2 text-xs font-bold text-white"
                            >
                                <Download size={14} />
                                Download
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

const formatRoleLabel = (role) => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const AdminNavbar = () => {
    const navigate = useNavigate();
    const { admin, setAdmin } = useAdminAuth();
    const { theme, toggleTheme } = useTheme();
    const isDesktopNav = useIsDesktopNav();

    const { logoUrl } = useMeta();
    const sidebarRef = useRef(null);
    const mobileSidebarRef = useRef(null);
    const [superAdminRequestOpen, setSuperAdminRequestOpen] = useState(false);

    // Aggressively prefetch ALL routes on initial load for instant navigation
    useEffect(() => {
        const timer = setTimeout(() => {
            const routesToPrefetch = [
                ...Object.keys(ADMIN_ROUTE_PREFETCH),
                "/admin/attendance",
                "/admin/employees"
            ];
            routesToPrefetch.forEach(route => prefetchAdminRoute(route));
        }, 800);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const restoreScroll = () => {
            const savedScroll = sessionStorage.getItem('adminSidebarScroll');
            if (savedScroll) {
                if (sidebarRef.current) sidebarRef.current.scrollTop = parseInt(savedScroll, 10);
                if (mobileSidebarRef.current) mobileSidebarRef.current.scrollTop = parseInt(savedScroll, 10);
            } else {
                const activeItem = document.querySelector('.admin-desktop-sidebar .active');
                if (activeItem && sidebarRef.current) {
                    activeItem.scrollIntoView({ block: 'center' });
                    if (sidebarRef.current) {
                        sessionStorage.setItem('adminSidebarScroll', sidebarRef.current.scrollTop);
                    }
                }
            }
        };

        // Run immediately, and queue for after layout paints
        restoreScroll();
        const t1 = setTimeout(restoreScroll, 10);
        const t2 = setTimeout(restoreScroll, 100);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            if (sidebarRef.current) {
                sessionStorage.setItem('adminSidebarScroll', sidebarRef.current.scrollTop);
            }
        };
    }, []);

    const handleSidebarScroll = (e) => {
        if (e.target.scrollTop >= 0) {
            sessionStorage.setItem('adminSidebarScroll', e.target.scrollTop);
        }
    };


    const [loggingOut, setLoggingOut] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        document.body.classList.add("admin-panel-active");
        return () => document.body.classList.remove("admin-panel-active");
    }, []);

    useEffect(() => {
        const startPrefetch = () => prefetchAdminAttendance();
        const timer = window.setTimeout(startPrefetch, 150);
        return () => window.clearTimeout(timer);
    }, []);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = "hidden";
            // Restore mobile sidebar scroll when it opens
            const savedScroll = sessionStorage.getItem('adminSidebarScroll');
            if (savedScroll && mobileSidebarRef.current) {
                mobileSidebarRef.current.scrollTop = parseInt(savedScroll, 10);
                setTimeout(() => {
                    if (mobileSidebarRef.current) {
                        mobileSidebarRef.current.scrollTop = parseInt(savedScroll, 10);
                    }
                }, 10);
            }
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
            if (mobileOpen && mobileSidebarRef.current) {
                sessionStorage.setItem('adminSidebarScroll', mobileSidebarRef.current.scrollTop);
            }
        };
    }, [mobileOpen]);

    const handleLogout = async () => {
        if (loggingOut) return;
        setLoggingOut(true);

        try {
            await api.post("/api/auth/logout");
        } catch (err) {
            console.error("Logout failed:", err);
        } finally {
            localStorage.removeItem("admin_token");
            setAdmin(null);
            navigate("/admin/login", { replace: true });
            setLoggingOut(false);
        }
    };

    const navSections = [
        {
            title: "Command",
            items: [
                {
                    to: "/admin/dashboard",
                    label: "Dashboard",
                    icon: <LayoutDashboard size={18} />,
                },
                {
                    to: "/admin/approval-inbox",
                    label: "Approval Inbox",
                    icon: <Inbox size={18} />,
                },
            ],
        },
        {
            title: "People & Time",
            items: [
                {
                    to: "/admin/employees",
                    label: "Employees",
                    icon: <Users size={18} />,
                },
                {
                    to: "/admin/attendance",
                    label: "Attendance",
                    icon: <CalendarCheck2 size={18} />,
                },
                {
                    to: "/admin/absence-without-leave",
                    label: "Absence Without Leave",
                    icon: <CalendarX2 size={18} />,
                },
                {
                    to: "/admin/escalations",
                    label: "Escalations",
                    icon: <ShieldAlert size={18} />,
                },
                {
                    to: "/admin/hr",
                    label: "HR Command",
                    icon: <Users size={18} />,
                },
                {
                    to: "/admin/general-letters",
                    label: "General Letters",
                    icon: <FileText size={18} />,
                },
                {
                    to: "/admin/sales-live-tracking",
                    label: "Sales Tracking",
                    icon: <MapPin size={18} />,
                },
                {
                    to: "/admin/wfh",
                    label: "WFH",
                    icon: <Home size={18} />,
                },
                {
                    to: "/admin/geofencing",
                    label: "Geofencing",
                    icon: <MapPin size={18} />,
                },
            ],
        },
        {
            title: "Leave Management",
            items: [
                {
                    to: "/admin/leave",
                    label: "Leave Applications",
                    icon: <Calendar size={18} />,
                },
                {
                    to: "/admin/leave-settings",
                    label: "Leave Settings",
                    icon: <Settings size={18} />,
                },
            ],
        },
        {
            title: "Workflows",
            items: [
                {
                    to: "/admin/eod",
                    label: "EOD Reports",
                    icon: <ClipboardList size={18} />,
                },
                {
                    to: "/admin/tasks",
                    label: "Tasks",
                    icon: <CheckSquare size={18} />,
                },

                {
                    to: "/admin/lunch-alerts",
                    label: "Lunch Alerts",
                    icon: <Coffee size={18} />,
                },
            ],
        },
        {
            title: "Requests",
            items: [
                {
                    to: "/admin/expenses",
                    label: "Expenses",
                    icon: <IndianRupee size={18} />,
                },
                {
                    to: "/admin/finance",
                    label: "Finance Command",
                    icon: <FileSpreadsheet size={18} />,
                },
                {
                    to: "/admin/complaints",
                    label: "Grievance",
                    icon: <ShieldAlert size={18} />,
                },
                {
                    to: "/admin/offboarding",
                    label: "Offboarding",
                    icon: <BriefcaseBusiness size={18} />,
                },
                {
                    to: "/admin/relieved-employees",
                    label: "Relieved Employees",
                    icon: <UserCheck size={18} />,
                },
            ],
        },
        {
            title: "Salary Management",
            items: [
                {
                    to: "/admin/payroll",
                    label: "Payroll",
                    icon: <IndianRupee size={18} />,
                },
                {
                    to: "/admin/control-center",
                    label: "Payroll Locking System",
                    icon: <FileBarChart size={18} />,
                },
                {
                    to: "/admin/salary-slip-settings",
                    label: "Salary Slip Settings",
                    icon: <FileSpreadsheet size={18} />,
                },
            ],
        },
        {
            title: "Company",
            items: [
                {
                    to: "/admin/department-efficiency",
                    label: "Department Efficiency",
                    icon: <Gauge size={18} />,
                },
                {
                    to: "/admin/announcements",
                    label: "Announcements",
                    icon: <Megaphone size={18} />,
                },
                {
                    to: "/admin/policies",
                    label: "Policies",
                    icon: <ShieldCheck size={18} />,
                },
                {
                    to: "/admin/qr-code",
                    label: "Office QR Code",
                    icon: <QrCode size={18} />,
                },
                {
                    to: "/admin/selfie-access",
                    label: "Selfie Access",
                    icon: <Camera size={18} />,
                },
                {
                    to: "/admin/settings",
                    label: "Settings",
                    icon: <Settings size={18} />,
                },
                {
                    to: "#",
                    label: "Speak with Super Admin",
                    icon: <Sparkles size={18} className="text-purple-500 animate-pulse" />,
                    onClick: (e) => {
                        if (e) e.preventDefault();
                        setSuperAdminRequestOpen(true);
                    },
                },
            ],
        },
    ];

    const navClass = ({ isActive }) => `
        relative
        flex
        items-center
        gap-3
        px-3
        py-2.5
        rounded-xl
        text-sm
        font-semibold
        transition-all
        duration-200
        border
        ${isActive
            ? `
            bg-[var(--accent-primary)]/8
            text-[var(--text-primary)]
            border-[var(--accent-primary)]/15
            shadow-sm
            before:absolute
            before:left-0
            before:top-1/2
            before:h-5
            before:w-1
            before:-translate-y-1/2
            before:rounded-r-full
            before:bg-[var(--accent-primary)]
            `
            : `
            border-transparent
            text-[var(--text-secondary)]
            hover:text-[var(--text-primary)]
            hover:bg-slate-100/80
            dark:hover:bg-[var(--card-bg)]/5
            `
        }
    `;

    return (
        <>
            {isDesktopNav && (
                <>
                    <style>
                        {`
                            @media (min-width: 1280px) {
                                .admin-desktop-topbar ~ main,
                                .admin-desktop-topbar ~ .flex-1 {
                                    padding-top: 5.5rem !important;
                                }
                            }
                        `}
                    </style>
                    <div className="admin-desktop-topbar fixed top-0 left-72 right-0 z-[90] hidden h-16 items-center justify-end gap-2 border-b border-slate-200 bg-[var(--card-bg)]/90 px-4 backdrop-blur-xl dark:border-white/10 dark:bg-[#111111]/90 xl:flex 2xl:gap-3 2xl:px-6">
                        <button
                            type="button"
                            onClick={() => setSuperAdminRequestOpen(true)}
                            className="flex items-center gap-1.5 rounded-full border border-purple-500/20 bg-purple-500/10 px-3.5 py-1.5 text-xs font-bold text-purple-600 hover:bg-purple-500/20 transition-all dark:text-purple-400"
                        >
                            <Sparkles size={14} className="animate-pulse text-purple-500" />
                            <span>Speak with Super Admin</span>
                        </button>
                        <div className="rounded-full border border-amber-200 bg-amber-50 p-1 shadow-md shadow-amber-500/10 dark:border-amber-400/20 dark:bg-amber-400/10">
                            <AdminNotificationBell />
                        </div>
                        <div className="rounded-full border border-[var(--accent-soft)] bg-[var(--accent-light)] p-1 shadow-md shadow-[var(--accent-primary)]/15 dark:border-[var(--accent-primary)]/20 dark:bg-[var(--accent-subtle)]">
                            <AdminChatPanel />
                        </div>
                    </div>
                </>
            )}

            {/* ================= DESKTOP SIDEBAR ================= */}
            <aside
                className="
                admin-desktop-sidebar
                hidden
                xl:flex
                flex-col
                w-72
                h-screen
                sticky
                top-0
                bg-[var(--bg-primary)]
                border-r
                border-[var(--border-color)]
                px-5
                pt-0
                pb-5
                z-30
                shrink-0
            "
            >
                    <div className="border-b border-slate-200 dark:border-white/10 flex items-center justify-center overflow-hidden py-4 px-3 mb-3">
                        <img
                            src={logoUrl || "/logo_light.png?v=3"}
                            alt="Savrion Technologies"
                            className="h-12 max-h-14 w-auto max-w-full object-contain dark:hidden"
                        />
                        <img
                            src={logoUrl || "/logo_dark.png?v=3"}
                            alt="Savrion Technologies"
                            className="h-12 max-h-14 w-auto max-w-full object-contain hidden dark:block"
                        />
                    </div>

                {/* NAV ITEMS */}
                <nav 
                    ref={sidebarRef}
                    onScroll={handleSidebarScroll}
                    className="flex-1 overflow-y-auto pr-1"
                >
                    <div className="space-y-2.5">
                        {navSections.map((section) => (
                            <div
                                key={section.title}
                                className="rounded-2xl border border-slate-100/80 bg-[var(--bg-secondary)]/55 p-1.5 dark:border-white/5 dark:bg-[var(--card-bg)]/[0.025]"
                            >
                                <div className="mb-1.5 flex items-center gap-2 px-2.5 py-1">
                                    <span className="h-px flex-1 bg-[var(--border-color)]" />
                                    <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[var(--heading-secondary)]">
                                        {section.title}
                                    </p>
                                    <span className="h-px flex-1 bg-[var(--border-color)]" />
                                </div>
                                <div className="space-y-1">
                                    {section.items.map((item) => (
                                        item.onClick ? (
                                            <button
                                                key={item.label}
                                                type="button"
                                                onClick={item.onClick}
                                                className="relative flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 border border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-slate-100/80 dark:hover:bg-[var(--card-bg)]/5"
                                            >
                                                {item.icon}
                                                <span>{item.label}</span>
                                            </button>
                                        ) : (
                                            <NavLink
                                                key={item.to}
                                                to={item.to}
                                                onMouseEnter={() => prefetchAdminRoute(item.to)}
                                                onFocus={() => prefetchAdminRoute(item.to)}
                                                className={navClass}
                                            >
                                                {item.icon}
                                                <span>{item.label}</span>
                                            </NavLink>
                                        )
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </nav>

                {/* BOTTOM SECTION */}
                <div className="pt-6 border-t border-[var(--border-color)] mt-auto space-y-4">
                    {/* THEME TOGGLE & PROFILE INFO */}
                    <div className="flex items-center justify-between px-2">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-[var(--accent-primary)] flex items-center justify-center text-white font-bold shrink-0 shadow-sm">
                                {admin?.name?.charAt(0)?.toUpperCase() || "A"}
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold truncate leading-none mb-1">
                                    {admin?.name || "Admin"}
                                </p>
                                <p className="text-[10px] text-[var(--text-secondary)] truncate">
                                    {formatRoleLabel(admin?.role) || "Administrator"}
                                </p>
                            </div>
                        </div>

                        <button
                          onClick={toggleTheme}
                          className="w-10 h-10 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] hover:bg-black/5 dark:hover:bg-[var(--card-bg)]/5 flex items-center justify-center transition-all shrink-0"
                        >
                            {theme === "dark" ? (
                                <Sun size={18} className="text-yellow-400" />
                            ) : (
                                <Moon size={18} className="text-gray-600" />
                            )}
                        </button>
                    </div>

                    {/* LOGOUT BUTTON */}
                    <button
                        onClick={handleLogout}
                        disabled={loggingOut}
                        className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold shadow-md shadow-red-500/10 transition-all active:scale-[0.98]"
                    >
                        <LogOut size={16} />
                        <span>{loggingOut ? "Logging..." : "Logout"}</span>
                    </button>
                </div>
            </aside>

            {/* ================= MOBILE HEADER ================= */}
            <header
                className="
                admin-mobile-header
                xl:hidden
                sticky
                top-0
                z-40
                backdrop-blur-xl
                bg-[var(--card-bg)]/80
                dark:bg-[#111111]/85
                border-b
                border-[var(--border-color)]
                h-[72px]
                w-full
                px-3
                sm:px-4
                flex
                items-center
                justify-between
            "
            >
                <div className="flex min-w-0 flex-1 items-center justify-center overflow-hidden px-4 py-2">
                    <img
                        src={logoUrl || "/logo_light.png?v=3"}
                        alt="Savrion Technologies"
                        className="w-full max-h-10 h-auto object-contain dark:hidden"
                    />
                    <img
                        src={logoUrl || "/logo_dark.png?v=3"}
                        alt="Savrion Technologies"
                        className="w-full max-h-10 h-auto object-contain hidden dark:block"
                    />
                </div>

                <div className="admin-mobile-actions flex shrink-0 items-center justify-end gap-1.5 sm:gap-2">
                    {!isDesktopNav && (
                        <>
                            <AdminNotificationBell />
                            <AdminChatPanel />
                        </>
                    )}
                    <button
                        onClick={toggleTheme}
                        className="admin-mobile-icon-button w-10 h-10 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] flex items-center justify-center"
                    >
                        {theme === "dark" ? (
                            <Sun size={16} className="text-yellow-400" />
                        ) : (
                            <Moon size={16} />
                        )}
                    </button>
                    <button
                        onClick={() => setMobileOpen(true)}
                        className="admin-mobile-icon-button w-10 h-10 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] flex items-center justify-center"
                    >
                        <Menu size={20} />
                    </button>
                </div>
            </header>

            {/* ================= MOBILE MENU OVERLAY ================= */}
            {mobileOpen && (
                <div className="fixed inset-0 z-[100] xl:hidden">
                    <div 
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
                        onClick={() => setMobileOpen(false)}
                    />
                    <div className="absolute bottom-0 right-0 top-0 z-10 flex w-[min(20rem,calc(100vw-1rem))] flex-col bg-[var(--bg-primary)] p-4 shadow-2xl transition-transform duration-300 sm:p-5">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-bold text-lg">Menu</h2>
                            <button onClick={() => setMobileOpen(false)} className="w-10 h-10 rounded-xl border border-[var(--border-color)] flex items-center justify-center">
                                <X size={20} />
                            </button>
                        </div>

                        <nav 
                            ref={mobileSidebarRef}
                            onScroll={handleSidebarScroll}
                            className="flex-1 overflow-y-auto"
                        >
                            <div className="space-y-2.5">
                                {navSections.map((section) => (
                                    <div
                                        key={section.title}
                                        className="rounded-2xl border border-slate-100/80 bg-[var(--bg-secondary)]/55 p-1.5 dark:border-white/5 dark:bg-[var(--card-bg)]/[0.025]"
                                    >
                                        <div className="mb-1.5 flex items-center gap-2 px-2.5 py-1">
                                            <span className="h-px flex-1 bg-[var(--border-color)]" />
                                            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[var(--heading-secondary)]">
                                                {section.title}
                                            </p>
                                            <span className="h-px flex-1 bg-[var(--border-color)]" />
                                        </div>
                                        <div className="space-y-1">
                                            {section.items.map((item) => (
                                                <NavLink
                                                    key={item.to}
                                                    to={item.to}
                                                    onTouchStart={() => prefetchAdminRoute(item.to)}
                                                    onFocus={() => prefetchAdminRoute(item.to)}
                                                    onClick={() => setMobileOpen(false)}
                                                    className={navClass}
                                                >
                                                    {item.icon}
                                                    <span>{item.label}</span>
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </nav>

                        <div className="pt-6 border-t border-[var(--border-color)] mt-auto space-y-4">
                            <div className="flex items-center gap-3 px-2">
                                <div className="w-10 h-10 rounded-xl bg-[var(--accent-primary)] flex items-center justify-center text-white font-bold shrink-0">
                                    {admin?.name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold truncate leading-none mb-1">{admin?.name}</p>
                                    <p className="text-xs text-[var(--text-secondary)] truncate">{formatRoleLabel(admin?.role) || "Administrator"}</p>
                                </div>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center justify-center gap-2.5 py-3 rounded-2xl bg-red-500 text-white font-bold shadow-md shadow-red-500/10"
                            >
                                <LogOut size={16} />
                                <span>Logout</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <SuperAdminRequestModal
                isOpen={superAdminRequestOpen}
                onClose={() => setSuperAdminRequestOpen(false)}
            />
        </>
    );
};

export default AdminNavbar;


